import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Pencil,
  Plus,
  Trash2,
  Variable,
  Volume2,
  Workflow,
} from "lucide-react";
import { PageHeader } from "../components/layout/PageHeader";
import { ConfirmModal } from "../components/ui/ConfirmModal";
import {
  ScheduleStateChip,
  formatDateTime,
} from "../components/schedule/ScheduleChips";
import {
  applicationsApi,
  type ApplicationDetail,
  type ApplicationScheduleItem,
} from "../api/applicationsApi";
import {
  numbersApi,
  SCRIPT_TYPE_IDS,
  type NumberListItem,
} from "../api/numbersApi";
import { formatPhoneNumber } from "../lib/phone";
import { HttpError } from "../api/http";

/** Same rule as the applications list (spCustomerApplicationDnis_List): only
 *  DNIS rows whose current script is FlowBuilder belong in this surface. */
function filterScheduleForFlowBuilder(
  rows: ApplicationScheduleItem[],
  numberRows: NumberListItem[],
): ApplicationScheduleItem[] {
  const flowBuilderDnisIds = new Set(
    numberRows
      .filter((n) => n.scriptTypeId === SCRIPT_TYPE_IDS.flowBuilder)
      .map((n) => n.dnisId),
  );
  return rows.filter((r) => flowBuilderDnisIds.has(r.dnisId));
}

type LoadState =
  | { status: "loading" }
  | {
      status: "ready";
      detail: ApplicationDetail;
      schedule: ApplicationScheduleItem[];
    }
  | { status: "error"; message: string };

/**
 * Per-application detail page. Mirrors the Number detail page's layout
 * but inverts the schedule perspective: an Application is the anchor
 * here, and each schedule row points at a DNIS instead of a flow. The
 * "Build" button hands control over to the FlowBuilder for the active
 * flow, leaving everything flow-internal in its existing surface.
 * Name edits happen inline in the hero; description is not surfaced in UI.
 */
export function ApplicationDetailPage() {
  const { applicationId: applicationParam } = useParams<{
    applicationId: string;
  }>();
  const applicationId = Number(applicationParam);
  const navigate = useNavigate();

  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [busy, setBusy] = useState(false);
  const [nameEditing, setNameEditing] = useState(false);
  const [nameEditValue, setNameEditValue] = useState("");
  const [nameSaveError, setNameSaveError] = useState<string | null>(null);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [deleteAppConfirmOpen, setDeleteAppConfirmOpen] = useState(false);
  const [scheduleRemovePending, setScheduleRemovePending] = useState<
    number | null
  >(null);

  // Cache existing application names so duplicate-detection can run
  // client-side before we round-trip the PUT. The server still re-checks
  // and a 409 is treated as the source of truth (the cache could be
  // stale by the time we save).
  const [knownNames, setKnownNames] = useState<Set<string>>(new Set());

  const refresh = useCallback(async () => {
    if (!Number.isFinite(applicationId) || applicationId <= 0) {
      setState({ status: "error", message: "Invalid application id." });
      return;
    }
    try {
      const [detail, schedule, numbers] = await Promise.all([
        applicationsApi.getDetail(applicationId),
        applicationsApi.getSchedule(applicationId),
        numbersApi.list(),
      ]);
      setState({
        status: "ready",
        detail,
        schedule: filterScheduleForFlowBuilder(
          schedule.items ?? [],
          numbers.items ?? [],
        ),
      });
      setNameEditing(false);
      setNameSaveError(null);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load application";
      setState({ status: "error", message });
    }
  }, [applicationId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Pre-load the existing names so the in-page validation can warn
  // immediately (we still trust the API's 409 if the cache is stale).
  useEffect(() => {
    let active = true;
    applicationsApi
      .list()
      .then((res) => {
        if (!active) return;
        const names = new Set<string>();
        for (const it of res.items ?? []) {
          if (it.applicationName) {
            names.add(it.applicationName.trim().toLowerCase());
          }
        }
        setKnownNames(names);
      })
      .catch(() => {
        /* duplicate-detection is best-effort */
      });
    return () => {
      active = false;
    };
  }, []);

  const saveApplicationName = useCallback(async () => {
    if (state.status !== "ready") return;
    const name = nameEditValue.trim();
    if (!name) {
      setNameSaveError("Name is required.");
      return;
    }
    const ownName = state.detail.applicationName?.trim().toLowerCase() ?? "";
    if (
      name.toLowerCase() !== ownName &&
      knownNames.has(name.toLowerCase())
    ) {
      setNameSaveError("Name is already in use.");
      return;
    }

    setNameSaveError(null);
    setBusy(true);
    try {
      const updated = await applicationsApi.update(applicationId, {
        applicationName: name,
        description: state.detail.description ?? "",
      });
      setState((prev) =>
        prev.status === "ready" ? { ...prev, detail: updated } : prev,
      );
      setNameEditing(false);
      setKnownNames((prev) => {
        const next = new Set(prev);
        if (ownName) next.delete(ownName);
        next.add(name.toLowerCase());
        return next;
      });
    } catch (err) {
      if (err instanceof HttpError && err.status === 409) {
        setKnownNames((prev) => {
          const next = new Set(prev);
          next.add(name.toLowerCase());
          return next;
        });
        setNameSaveError("Name is already in use.");
      } else {
        setNameSaveError(
          err instanceof Error ? err.message : "Failed to save name",
        );
      }
    } finally {
      setBusy(false);
    }
  }, [applicationId, knownNames, nameEditValue, state]);

  const startNameEdit = useCallback(() => {
    if (state.status !== "ready") return;
    setNameEditValue(state.detail.applicationName ?? "");
    setNameSaveError(null);
    setNameEditing(true);
  }, [state]);

  const cancelNameEdit = useCallback(() => {
    setNameEditing(false);
    setNameSaveError(null);
  }, []);

  const onAddSchedule = useCallback(
    async (dnisId: number, validFromUtc: string | null) => {
      setScheduleError(null);
      setBusy(true);
      try {
        const next = await applicationsApi.addSchedule(applicationId, {
          dnisId,
          validFromUtc,
        });
        const numbers = await numbersApi.list();
        setState((prev) =>
          prev.status === "ready"
            ? {
                ...prev,
                schedule: filterScheduleForFlowBuilder(
                  next.items ?? [],
                  numbers.items ?? [],
                ),
              }
            : prev,
        );
        setShowAdd(false);
      } catch (err) {
        setScheduleError(
          err instanceof Error ? err.message : "Failed to add schedule",
        );
      } finally {
        setBusy(false);
      }
    },
    [applicationId],
  );

  const runRemoveSchedule = useCallback(
    async (applicationDnisId: number) => {
      setScheduleError(null);
      setBusy(true);
      try {
        const next = await applicationsApi.deleteSchedule(
          applicationId,
          applicationDnisId,
        );
        const numbers = await numbersApi.list();
        setState((prev) =>
          prev.status === "ready"
            ? {
                ...prev,
                schedule: filterScheduleForFlowBuilder(
                  next.items ?? [],
                  numbers.items ?? [],
                ),
              }
            : prev,
        );
      } catch (err) {
        setScheduleError(
          err instanceof Error ? err.message : "Failed to delete schedule",
        );
      } finally {
        setBusy(false);
      }
    },
    [applicationId],
  );

  const requestRemoveSchedule = useCallback((applicationDnisId: number) => {
    setScheduleRemovePending(applicationDnisId);
  }, []);

  const blockedByActive = useMemo(() => {
    if (state.status !== "ready") return false;
    return state.schedule.some((row) => {
      const s = (row.stateName || "").toLowerCase();
      return s === "active" || s === "scheduled";
    });
  }, [state]);

  const performDeleteApplication = useCallback(async () => {
    if (state.status !== "ready") return;
    if (blockedByActive) return;
    setDeleteAppConfirmOpen(false);
    setDeleteError(null);
    setBusy(true);
    try {
      await applicationsApi.remove(applicationId);
      navigate("/applications");
    } catch (err) {
      if (err instanceof HttpError && err.status === 409) {
        setDeleteError(
          "This application still has active or scheduled numbers. Remove them first.",
        );
        await refresh();
      } else {
        setDeleteError(
          err instanceof Error ? err.message : "Failed to delete application",
        );
      }
    } finally {
      setBusy(false);
    }
  }, [applicationId, blockedByActive, navigate, refresh, state]);

  const requestDeleteApplication = useCallback(() => {
    setDeleteAppConfirmOpen(true);
  }, []);

  const title =
    state.status === "ready"
      ? nameEditing
        ? nameEditValue.trim() || "Application"
        : state.detail.applicationName || "Application"
      : "Application";

  const nameSaveDisabled = useMemo(() => {
    if (state.status !== "ready" || busy) return true;
    const trimmed = nameEditValue.trim();
    if (!trimmed) return true;
    return trimmed === (state.detail.applicationName ?? "").trim();
  }, [busy, nameEditValue, state]);

  return (
    <>
      <PageHeader
        title={title}
        actions={
          <Link
            to="/applications"
            aria-label="Back to applications"
            className="btn btn-secondary" style={{ display: "inline-flex", alignItems: "center", gap: 6, textDecoration: "none" }}
          >
            <ArrowLeft size={14} /> Back to applications
          </Link>
        }
      />
      <div className="dashboard">
        {state.status === "loading" && (
          <div className="tile" style={{ color: "var(--portal-muted)" }}>
            Loading application…
          </div>
        )}
        {state.status === "error" && (
          <div
            className="tile"
            style={{
              borderColor: "#e74c3c",
              color: "#c0392b",
              background: "#fdecea",
            }}
          >
            <strong>Could not load this application.</strong>
            <div style={{ marginTop: 6, fontSize: "0.9rem" }}>
              {state.message}
            </div>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ marginTop: 12 }}
              onClick={() => navigate("/applications")}
            >
              Back to applications
            </button>
          </div>
        )}
        {state.status === "ready" && (
          <>
            <ApplicationHero
              detail={state.detail}
              dnisNumbers={state.schedule
                .filter(
                  (s) => (s.stateName || "").toLowerCase() === "active",
                )
                .map((s) => s.dnisPresentation)}
              onBuild={() =>
                navigate(`/applications/${applicationId}/design`)
              }
              nameEditing={nameEditing}
              nameEditValue={nameEditValue}
              onNameEditChange={setNameEditValue}
              onStartNameEdit={startNameEdit}
              onCancelNameEdit={cancelNameEdit}
              onSaveNameEdit={() => void saveApplicationName()}
              nameSaveBusy={busy}
              nameSaveDisabled={nameSaveDisabled}
              nameSaveError={nameSaveError}
            />

            <VoiceVariablesTiles applicationId={applicationId} />

            <ScheduleTile
              rows={state.schedule}
              busy={busy}
              error={scheduleError}
              onAdd={() => setShowAdd(true)}
              onDelete={requestRemoveSchedule}
            />

            <DangerZoneTile
              busy={busy}
              blocked={blockedByActive}
              error={deleteError}
              onRequestDelete={requestDeleteApplication}
            />
          </>
        )}

        {showAdd && (
          <ScheduleNumberModal
            existingDnisIds={
              state.status === "ready"
                ? state.schedule.map((s) => s.dnisId)
                : []
            }
            onCancel={() => setShowAdd(false)}
            onConfirm={onAddSchedule}
            busy={busy}
          />
        )}
      </div>

      {state.status === "ready" && scheduleRemovePending != null && (
        <ConfirmModal
          open
          onClose={() => {
            if (!busy) setScheduleRemovePending(null);
          }}
          title="Remove scheduled assignment?"
          confirmLabel="Remove"
          tone="caution"
          busy={busy}
          onConfirm={() => {
            const id = scheduleRemovePending;
            setScheduleRemovePending(null);
            if (id != null) void runRemoveSchedule(id);
          }}
        >
          Remove the scheduled assignment for{" "}
          <strong>
            {state.schedule.find((s) => s.applicationDnisId === scheduleRemovePending)
              ?.dnisPresentation ?? "this number"}
          </strong>
          ? The number will no longer be routed to this application according
          to this schedule entry.
        </ConfirmModal>
      )}

      {state.status === "ready" && (
        <ConfirmModal
          open={deleteAppConfirmOpen}
          onClose={() => {
            if (!busy) setDeleteAppConfirmOpen(false);
          }}
          title="Delete application?"
          confirmLabel="Delete"
          tone="danger"
          busy={busy}
          onConfirm={() => void performDeleteApplication()}
        >
          Delete application &quot;{state.detail.applicationName}&quot;? This
          cannot be undone. You can only delete when no number is actively
          or scheduled to route here.
        </ConfirmModal>
      )}
    </>
  );
}

function ApplicationHero({
  detail,
  dnisNumbers,
  onBuild,
  nameEditing,
  nameEditValue,
  onNameEditChange,
  onStartNameEdit,
  onCancelNameEdit,
  onSaveNameEdit,
  nameSaveBusy,
  nameSaveDisabled,
  nameSaveError,
}: {
  detail: ApplicationDetail;
  dnisNumbers: string[];
  onBuild: () => void;
  nameEditing: boolean;
  nameEditValue: string;
  onNameEditChange: (value: string) => void;
  onStartNameEdit: () => void;
  onCancelNameEdit: () => void;
  onSaveNameEdit: () => void;
  nameSaveBusy: boolean;
  nameSaveDisabled: boolean;
  nameSaveError: string | null;
}) {
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!nameEditing) return;
    const el = nameInputRef.current;
    if (!el) return;
    el.focus();
    el.select();
  }, [nameEditing]);

  return (
    <section
      className="tile"
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) auto",
        alignItems: "center",
        gap: 24,
        padding: 24,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          minWidth: 0,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 56,
            height: 56,
            borderRadius: 14,
            background: "var(--portal-primary-soft)",
            color: "var(--portal-primary-darker)",
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
          }}
        >
          <Workflow size={28} />
        </span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
              minWidth: 0,
            }}
          >
            {nameEditing ? (
              <>
                <input
                  ref={nameInputRef}
                  type="text"
                  value={nameEditValue}
                  onChange={(e) => onNameEditChange(e.target.value)}
                  disabled={nameSaveBusy}
                  maxLength={50}
                  aria-label="Application name"
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      e.preventDefault();
                      onCancelNameEdit();
                    } else if (e.key === "Enter") {
                      e.preventDefault();
                      if (!nameSaveDisabled && !nameSaveBusy) onSaveNameEdit();
                    }
                  }}
                  style={{
                    ...inputStyle,
                    flex: "1 1 200px",
                    minWidth: 120,
                    maxWidth: "100%",
                    fontSize: "1.35rem",
                    fontWeight: 700,
                    letterSpacing: "0.01em",
                  }}
                />
                <div style={{ display: "inline-flex", gap: 6, flexShrink: 0 }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={onCancelNameEdit}
                    disabled={nameSaveBusy}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={onSaveNameEdit}
                    disabled={nameSaveDisabled || nameSaveBusy}
                  >
                    {nameSaveBusy ? "Saving…" : "Save"}
                  </button>
                </div>
              </>
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  minWidth: 0,
                }}
              >
                <span
                  style={{
                    fontSize: "1.6rem",
                    fontWeight: 700,
                    letterSpacing: "0.01em",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={detail.applicationName}
                >
                  {detail.applicationName || "—"}
                </span>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onStartNameEdit}
                  disabled={nameSaveBusy}
                  aria-label="Edit application name"
                  title="Edit name"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "4px 6px",
                    minWidth: 0,
                  }}
                >
                  <Pencil size={16} aria-hidden />
                </button>
              </div>
            )}
          </div>
          {nameSaveError && nameEditing && (
            <div
              style={{
                marginTop: 8,
                color: "#c0392b",
                fontSize: "0.85rem",
              }}
            >
              {nameSaveError}
            </div>
          )}
          <div
            style={{
              color: "var(--portal-muted)",
              fontSize: "0.95rem",
              marginTop: 2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {dnisNumbers.length === 0
              ? "Not currently routed to any number"
              : dnisNumbers.length === 1
                ? `Active on ${dnisNumbers[0]}`
                : `Active on ${dnisNumbers.length} numbers`}
            {detail.createdUtc && (
              <>
                {" · Created "}
                {formatShortDate(detail.createdUtc)}
              </>
            )}
          </div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button
          type="button"
          className="btn btn-primary"
          onClick={onBuild}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
          }}
          aria-label="Open this application in the Flow Builder"
        >
          <Pencil size={16} /> Build
        </button>
      </div>
    </section>
  );
}

function VoiceVariablesTiles({ applicationId }: { applicationId: number }) {
  const base = `/applications/${applicationId}`;
  return (
    <section
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
        gap: 12,
      }}
      aria-label="Application resources"
    >
      <ApplicationResourceQuickLink
        to={`${base}/voice`}
        icon={<Volume2 size={18} />}
        title="Voice"
        subtitle="Voice prompts used in this application"
      />
      <ApplicationResourceQuickLink
        to={`${base}/variables`}
        icon={<Variable size={18} />}
        title="Variables"
        subtitle="Call variables and flow data"
      />
    </section>
  );
}

/** Same horizontal tile pattern as the number detail page QuickLink. */
function ApplicationResourceQuickLink({
  to,
  icon,
  title,
  subtitle,
}: {
  to: string;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
}) {
  return (
    <Link
      to={to}
      className="tile tile-link"
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: 14,
        padding: 16,
        textDecoration: "none",
        color: "inherit",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 40,
          height: 40,
          borderRadius: 12,
          background: "var(--portal-primary-soft)",
          color: "var(--portal-primary-darker)",
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600 }}>{title}</div>
        <div
          style={{
            color: "var(--portal-muted)",
            fontSize: "0.85rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {subtitle}
        </div>
      </div>
    </Link>
  );
}

function ScheduleTile({
  rows,
  busy,
  error,
  onAdd,
  onDelete,
}: {
  rows: ApplicationScheduleItem[];
  busy: boolean;
  error: string | null;
  onAdd: () => void;
  onDelete: (applicationDnisId: number) => void;
}) {
  return (
    <section
      className="tile"
      aria-labelledby="numbers-heading"
      style={{ gap: 12 }}
    >
      <header
        className="tile-head"
        style={{ display: "flex", alignItems: "center", gap: 12 }}
      >
        <h3 id="numbers-heading" style={{ margin: 0 }}>
          Numbers
        </h3>
        <button
          type="button"
          className="btn btn-primary"
          onClick={onAdd}
          disabled={busy}
          title="Schedule a number to route into this application"
          style={{
            marginLeft: "auto",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <Plus size={16} /> Schedule number
        </button>
      </header>
      <p
        style={{
          margin: 0,
          color: "var(--portal-muted)",
          fontSize: "0.9rem",
        }}
      >
        These numbers route to this application now or are scheduled to in the
        future.
      </p>
      {error && (
        <div
          style={{
            color: "#c0392b",
            background: "#fdecea",
            border: "1px solid #e74c3c",
            borderRadius: 8,
            padding: 10,
            fontSize: "0.9rem",
          }}
        >
          {error}
        </div>
      )}
      {rows.length === 0 ? (
        <div style={{ color: "var(--portal-muted)" }}>
          No numbers route to this application yet.
        </div>
      ) : (
        <ScheduleTable rows={rows} onDelete={onDelete} busy={busy} />
      )}
    </section>
  );
}

function ScheduleTable({
  rows,
  onDelete,
  busy,
}: {
  rows: ApplicationScheduleItem[];
  onDelete: (applicationDnisId: number) => void;
  busy: boolean;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns:
          "minmax(0, 2fr) auto minmax(0, 1.2fr) auto",
        rowGap: 6,
        columnGap: 16,
        alignItems: "center",
      }}
    >
      <Header>Number</Header>
      <Header>State</Header>
      <Header>Valid from (UTC)</Header>
      <div />

      {rows.map((row) => (
        <ScheduleRow
          key={row.applicationDnisId}
          row={row}
          onDelete={onDelete}
          busy={busy}
        />
      ))}
    </div>
  );
}

function ScheduleRow({
  row,
  onDelete,
  busy,
}: {
  row: ApplicationScheduleItem;
  onDelete: (applicationDnisId: number) => void;
  busy: boolean;
}) {
  const dim = (row.stateName || "").toLowerCase() === "expired";
  const formatted =
    formatPhoneNumber(row.dnisPresentation) || row.dnisPresentation || "—";
  return (
    <>
      <div
        style={{
          fontWeight: 500,
          color: dim ? "var(--portal-muted)" : "var(--portal-text)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={formatted}
      >
        <Link
          to={`/numbers/${row.dnisId}`}
          style={{
            color: "inherit",
            textDecoration: "none",
            fontFamily:
              "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: "0.95rem",
          }}
        >
          {formatted}
        </Link>
      </div>
      <ScheduleStateChip state={row.stateName} />
      <div
        style={{
          color: "var(--portal-muted)",
          fontSize: "0.9rem",
        }}
      >
        {formatDateTime(row.validFromUtc)}
      </div>
      <div style={{ textAlign: "right" }}>
        {row.allowDelete && (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => onDelete(row.applicationDnisId)}
            disabled={busy}
            title="Remove this scheduled assignment"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: "0.85rem",
            }}
          >
            <Trash2 size={14} /> Remove
          </button>
        )}
      </div>
    </>
  );
}

function DangerZoneTile({
  busy,
  blocked,
  error,
  onRequestDelete,
}: {
  busy: boolean;
  blocked: boolean;
  error: string | null;
  onRequestDelete: () => void;
}) {
  const disabled = busy || blocked;
  const tooltip = blocked
    ? "Remove all active and scheduled numbers before deleting this application."
    : "Soft-delete this application (it can be restored from the database)";
  // Match the Destination "Archive" tile: a regular tile (no coloured
  // border, no warning icon) with a white-on-coloured-border button.
  // The destructive intent is signalled by colour only when the action
  // is *available* — when it's blocked or in flight the button goes
  // entirely neutral so the eye doesn't pull toward an action that
  // wouldn't fire anyway.
  return (
    <section className="tile" aria-labelledby="danger-heading">
      <header className="tile-head">
        <h3 id="danger-heading">Delete</h3>
      </header>
      <p style={{ color: "var(--portal-muted)", margin: "0 0 12px 0" }}>
        Deleting an application is a soft-delete: existing recordings and
        history stay intact. You can only delete an application that no number
        is currently routing into.
      </p>
      {error && (
        <div
          style={{
            marginBottom: 12,
            color: "#c0392b",
            background: "#fdecea",
            border: "1px solid #e74c3c",
            borderRadius: 8,
            padding: 10,
            fontSize: "0.9rem",
          }}
        >
          {error}
        </div>
      )}
      <button
        type="button"
        className="btn"
        onClick={onRequestDelete}
        disabled={disabled}
        title={tooltip}
        style={{
          background: "#ffffff",
          color: disabled ? "var(--portal-muted)" : "#c0392b",
          borderColor: disabled ? "var(--portal-border)" : "#e74c3c",
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <Trash2 size={16} />
        Delete application
      </button>
    </section>
  );
}

function ScheduleNumberModal({
  existingDnisIds,
  onCancel,
  onConfirm,
  busy,
}: {
  existingDnisIds: number[];
  onCancel: () => void;
  onConfirm: (dnisId: number, validFromUtc: string | null) => void;
  busy: boolean;
}) {
  const [numbers, setNumbers] = useState<NumberListItem[]>([]);
  const [numbersLoading, setNumbersLoading] = useState(true);
  const [numbersError, setNumbersError] = useState<string | null>(null);
  const [dnisId, setDnisId] = useState<number | "">("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");

  useEffect(() => {
    let active = true;
    numbersApi
      .list()
      .then((res) => {
        if (!active) return;
        // Only DNIS rows that already route through FlowBuilder are
        // candidates: any other scriptType means the binding wouldn't
        // actually deliver calls (see Scripts/02-customer-application-dnis
        // for the rationale on why mismatched bindings are filtered out
        // of the list view too).
        const filtered = (res.items ?? [])
          .filter(
            (n) => n.scriptTypeId === SCRIPT_TYPE_IDS.flowBuilder,
          )
          .slice()
          .sort((a, b) =>
            (a.number ?? "").localeCompare(b.number ?? "", "en", {
              sensitivity: "base",
            }),
          );
        setNumbers(filtered);
      })
      .catch((err: unknown) => {
        if (!active) return;
        setNumbersError(
          err instanceof Error ? err.message : "Failed to load numbers",
        );
      })
      .finally(() => {
        if (active) setNumbersLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const validFrom = useMemo(() => {
    if (!date) return null;
    // Same convention as the Number-side modal: combine date+time as a
    // local-time ISO and let the SP receive it as UTC. Customers
    // schedule in their own clock; this matches the legacy behaviour.
    const t = time ? `${time}:00` : "00:00:00";
    return `${date}T${t}Z`;
  }, [date, time]);

  // Already-bound numbers stay in the dropdown but are visually marked
  // and disabled — that way the customer can see immediately why their
  // candidate isn't selectable instead of wondering where it went.
  const existingSet = useMemo(
    () => new Set(existingDnisIds),
    [existingDnisIds],
  );

  const canConfirm =
    !busy && !numbersLoading && dnisId !== "" && Number(dnisId) > 0;

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) {
        e.stopPropagation();
        onCancel();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [busy, onCancel]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="schedule-number-title"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.55)",
        display: "grid",
        placeItems: "center",
        zIndex: 50,
      }}
    >
      <div
        className="tile"
        style={{
          maxWidth: 480,
          width: "min(92vw, 480px)",
          padding: 24,
          gap: 16,
        }}
      >
        <h3 id="schedule-number-title" style={{ margin: 0 }}>
          Schedule a number
        </h3>

        {numbersError && (
          <div
            style={{
              color: "#c0392b",
              background: "#fdecea",
              border: "1px solid #e74c3c",
              borderRadius: 8,
              padding: 10,
              fontSize: "0.9rem",
            }}
          >
            {numbersError}
          </div>
        )}

        <Field label="Number">
          <select
            value={dnisId}
            onChange={(e) =>
              setDnisId(e.target.value === "" ? "" : Number(e.target.value))
            }
            disabled={numbersLoading}
            style={inputStyle}
          >
            <option value="">
              {numbersLoading
                ? "Loading…"
                : numbers.length === 0
                  ? "No FlowBuilder numbers available"
                  : "Select number"}
            </option>
            {numbers.map((n) => {
              const formatted =
                formatPhoneNumber(n.number, n.countryCode, n.areaCode) ||
                n.number;
              const taken = existingSet.has(n.dnisId);
              return (
                <option
                  key={n.dnisId}
                  value={n.dnisId}
                  disabled={taken}
                >
                  {formatted}
                  {taken ? " · already scheduled" : ""}
                </option>
              );
            })}
          </select>
        </Field>

        <Field label="Date (optional, leave empty for now)">
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={inputStyle}
          />
        </Field>

        <Field label="Time (optional, UTC)">
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            style={inputStyle}
          />
        </Field>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            marginTop: 4,
          }}
        >
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => onConfirm(Number(dnisId), validFrom)}
            disabled={!canConfirm}
          >
            {busy ? "Scheduling…" : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Header({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        color: "var(--portal-muted)",
        fontSize: "0.75rem",
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        paddingBottom: 6,
        borderBottom: "1px solid var(--portal-border)",
      }}
    >
      {children}
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: "block" }}>
      <div
        style={{
          color: "var(--portal-muted)",
          fontSize: "0.8rem",
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      {children}
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "8px 12px",
  border: "1px solid var(--portal-border)",
  borderRadius: 6,
  fontSize: "0.95rem",
  fontFamily: "inherit",
  background: "white",
};

function formatShortDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}
